import { logEvent } from 'firebase/analytics';

import {
  type BuildRequirementsType,
  type UserQuarry,
} from '@/common/types/quarryType';
import {
  type ProductData,
  type UserWorkshop,
  type WorkshopRequirements,
} from '@/common/types/workshopType';
import { analytics } from '@/firebase';

import { type UserFactory } from '../types/factoryType';
import { type Storage } from '../types/userType';

const findTargetGood = (obj: UserWorkshop): ProductData | undefined =>
  obj.products.find((el) => el.name === obj.targetProduct);

function firebaseLogUpgradeBuilding(
  {
    name,
    id,
    level,
  }: {
    name: string;
    id: string;
    level: number;
  },
  building: BuildRequirementsType | WorkshopRequirements,
): void {
  if (!analytics) return;
  logEvent(analytics, 'building_upgrade', {
    building_id: id,
    building_name: name,
    previous_level: level - 1,
    new_level: level,
  });

  Object.entries({
    cash: building.cash,
    ...(building.materials ?? {}),
  }).forEach(([currency_type, amount]) => {
    if (!analytics) return;
    logEvent(analytics, 'currency_spent', {
      currency_type,
      amount,
      reason: 'building_upgrade',
      building_id: id,
    });
  });
}

type SingleResourceObj = UserQuarry | UserFactory;

type MultipleResourcesObj = Record<string, SingleResourceObj>;

const isSingleResourceObj = (
  obj: SingleResourceObj | MultipleResourcesObj,
): obj is SingleResourceObj => 'id' in obj;

function firebaseLogCollectResource(
  objResource: SingleResourceObj | MultipleResourcesObj,
  newStorage: Storage | undefined,
  prevStorage: Storage | undefined,
): void {
  if (!newStorage || !prevStorage) return;

  const collectResource = (obj: SingleResourceObj): void => {
    const amount =
      (newStorage[obj.name]?.value ?? 0) - (prevStorage[obj.name]?.value ?? 0);

    if (amount <= 0 || !analytics) return;

    logEvent(analytics, 'resource_collected', {
      source: obj.name,
      building_id: obj.id,
      amount,
      storage_full: amount === objResource.storageCapacity,
      view_ads: false,
    });
    logEvent(analytics, 'currency_earned', {
      currency_type: objResource.name,
      amount,
      source: 'claim',
      view_ads: false,
    });
  };

  if (isSingleResourceObj(objResource)) {
    collectResource(objResource);
  } else {
    Object.values(objResource).forEach(collectResource);
  }
}

function firebaseLogCollectResourceWorkshop(
  objResource: UserWorkshop | UserWorkshop[],
  producted?: number,
): void {
  const collectFromWorkshop = (workshop: UserWorkshop): void => {
    const good = findTargetGood(workshop);

    if (!producted || !analytics || !good) return;

    logEvent(analytics, 'resource_collected', {
      source: good.name,
      building_id: good.id,
      amount: producted,
      storage_full: producted === good.storageCapacity,
      view_ads: false,
    });
    logEvent(analytics, 'currency_earned', {
      currency_type: good.name,
      amount: producted,
      source: 'claim',
      view_ads: false,
    });
  };

  if (Array.isArray(objResource)) {
    objResource.forEach(collectFromWorkshop);
  } else {
    collectFromWorkshop(objResource);
  }
}

function firebaseLogResourceProcessing(
  objResource: UserFactory | UserWorkshop,
  amount: number | { [key: string]: number },
): void {
  if (!analytics) return;

  if ('products' in objResource) {
    const good = findTargetGood(objResource);

    if (!good) return;

    Object.keys(good.recipeForProduction).forEach((name) => {
      const resourceAmount = typeof amount === 'object' ? amount[name] : 0;

      if (!analytics) return;
      logEvent(analytics, 'currency_spent', {
        currency_type: name,
        amount: resourceAmount,
        reason: 'production_goods',
        item_id: good.name,
      });
    });
  } else {
    logEvent(analytics, 'currency_spent', {
      currency_type: objResource.source,
      amount: amount,
      reason: 'resource_conversion',
      target_resource: objResource.name,
      conversion_rate: objResource.rate,
    });
  }
}

export {
  firebaseLogCollectResource,
  firebaseLogCollectResourceWorkshop,
  firebaseLogResourceProcessing,
  firebaseLogUpgradeBuilding,
};
